/**
 * Feature Detection & Browser Capability — hand gesture controls.
 *
 * Detects whether the current browser environment exposes every API required
 * to run the MediaPipe-based hand gesture pipeline (camera access, Web Workers,
 * WebAssembly, and off-thread image decoding via createImageBitmap).
 */

/**
 * Returns true only when ALL APIs required for hand gesture controls are
 * available in the current environment:
 * - navigator with mediaDevices.getUserMedia (camera access)
 * - Worker (off-main-thread inference)
 * - WebAssembly (MediaPipe runtime)
 * - createImageBitmap (efficient frame decoding)
 *
 * Guards against environments where `navigator` exists but `mediaDevices` is
 * absent, so the check never throws.
 */
export function isHandGestureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'mediaDevices' in navigator &&
    Boolean(navigator.mediaDevices) &&
    'getUserMedia' in navigator.mediaDevices &&
    typeof Worker !== 'undefined' &&
    typeof WebAssembly !== 'undefined' &&
    typeof createImageBitmap !== 'undefined'
  );
}

/**
 * Resolves the current camera permission state via the Permissions API.
 * Falls back to 'prompt' when the API is unavailable or the query throws.
 */
export async function getCameraPermissionStatus(): Promise<PermissionState> {
  try {
    const result = await navigator.permissions.query({
      name: 'camera' as PermissionName,
    });
    return result.state;
  } catch {
    return 'prompt';
  }
}
